import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { DataTable } from '@chiroup/components/ui/data-table';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import {
  ArrowDown as ArrowDownIcon,
  ArrowUpDown as ArrowsUpDownIcon,
  ArrowUp as ArrowUpIcon,
  Bookmark as BookmarkIcon,
  Share as ShareIcon,
  XCircle as XCircleIcon,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import useResearchArticleDetail from '../../hooks/useResearchArticleDetail';
import { ResearchAnalyticsUser } from '../../services/research/researchAnalytics.service';

type ActionTab = 'viewed' | 'saved' | 'dismissed' | 'shared';

type SortHeaderProps = {
  field: string;
  label: string;
  activeSort: string;
  activeSortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
};

const SortHeader: React.FC<SortHeaderProps> = ({
  field,
  label,
  activeSort,
  activeSortDir,
  onSort,
}) => {
  const isActive = activeSort === field;
  return (
    <button
      onClick={() => onSort(field)}
      className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
    >
      {label}
      {isActive ? (
        activeSortDir === 'desc' ? (
          <ArrowDownIcon className="w-3 h-3 text-primary-500" />
        ) : (
          <ArrowUpIcon className="w-3 h-3 text-primary-500" />
        )
      ) : (
        <ArrowsUpDownIcon className="w-3 h-3 opacity-30" />
      )}
    </button>
  );
};

type Props = {
  newsId: number;
  title: string;
  createDate: string;
  viewCount: number;
  saveCount: number;
  dismissCount: number;
  shareCount: number;
};

const TABS: { key: ActionTab; label: string; icon: React.ReactNode; countKey: keyof Props }[] = [
  {
    key: 'viewed',
    label: 'Viewed',
    icon: <Icon icon={Icons.eye} className="w-4 h-4" />,
    countKey: 'viewCount',
  },
  {
    key: 'saved',
    label: 'Saved',
    icon: <BookmarkIcon className="w-4 h-4" />,
    countKey: 'saveCount',
  },
  {
    key: 'dismissed',
    label: 'Dismissed',
    icon: <XCircleIcon className="w-4 h-4" />,
    countKey: 'dismissCount',
  },
  {
    key: 'shared',
    label: 'Shared',
    icon: <ShareIcon className="w-4 h-4" />,
    countKey: 'shareCount',
  },
];

const StatBadge: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}> = ({ label, value, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
      active
        ? 'border-primary-300 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-700'
        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
    }`}
  >
    <span
      className={
        active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
      }
    >
      {icon}
    </span>
    <div className="text-left">
      <p
        className={`text-lg font-bold tabular-nums ${active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-gray-100'}`}
      >
        {value}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  </button>
);

const ResearchAnalyticsDetailModal: React.FC<Props> = ({
  newsId,
  title,
  createDate,
  viewCount,
  saveCount,
  dismissCount,
  shareCount,
}) => {
  const [activeTab, setActiveTab] = useState<ActionTab>('viewed');
  const counts = { newsId, title, createDate, viewCount, saveCount, dismissCount, shareCount };

  const { data, totalItems, page, pageSize, sort, sortDir, isFetching, setPage, handleSort } =
    useResearchArticleDetail(newsId, activeTab);

  const columns: ColumnDef<ResearchAnalyticsUser>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: () => (
          <SortHeader
            field="name"
            label="User"
            activeSort={sort}
            activeSortDir={sortDir}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <div>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {row.original.name || 'Unknown'}
            </span>
            {row.original.clinicName && (
              <p className="text-xs text-gray-400 dark:text-gray-500">{row.original.clinicName}</p>
            )}
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'actionDate',
        header: () => (
          <SortHeader
            field="actionDate"
            label="Date"
            activeSort={sort}
            activeSortDir={sortDir}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <span className="text-gray-500 dark:text-gray-400 text-sm tabular-nums">
            {row.original.actionDate
              ? dayjs(row.original.actionDate).format('MMM D, YYYY h:mm A')
              : '—'}
          </span>
        ),
        enableSorting: false,
      },
      ...(activeTab === 'shared'
        ? [
            {
              accessorKey: 'sharedCount' as const,
              header: () => (
                <SortHeader
                  field="sharedCount"
                  label="Times shared"
                  activeSort={sort}
                  activeSortDir={sortDir}
                  onSort={handleSort}
                />
              ),
              cell: ({ row }: { row: { original: ResearchAnalyticsUser } }) => (
                <span className="text-gray-500 dark:text-gray-400 text-sm tabular-nums">
                  {row.original.sharedCount || 0}
                </span>
              ),
              enableSorting: false,
            } as ColumnDef<ResearchAnalyticsUser>,
          ]
        : []),
    ],
    [activeTab, sort, sortDir, handleSort]
  );

  return (
    <div className="space-y-5">
      {/* Stat badges — clickable to switch tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {TABS.map(tab => (
          <StatBadge
            key={tab.key}
            label={tab.label}
            value={counts[tab.countKey] as number}
            icon={tab.icon}
            active={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
          />
        ))}
      </div>

      {/* User list table */}
      <DataTable
        columns={columns}
        data={data}
        loading={isFetching}
        size="compact"
        variant="minimal"
        emptyState={
          <div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
            No users found for this action.
          </div>
        }
        pagination={
          totalItems > pageSize
            ? {
                manual: true,
                pageIndex: page,
                pageSize,
                pageCount: Math.ceil(totalItems / pageSize),
                totalItems,
                onPageChange: setPage,
              }
            : false
        }
      />
    </div>
  );
};

export default ResearchAnalyticsDetailModal;
